import React, { useState, useRef, useEffect } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { UploadSimple, FileText, Check, Warning } from '@phosphor-icons/react';

export default function ImportsPage() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/admin/import-batches').then(r => { setBatches(r.data.items); setLoading(false); }).catch(() => setLoading(false));
  }, [importResult]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/companies/import-preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview({ ...data, file, fileName: file.name });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleImport = async () => {
    if (!preview?.file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', preview.file);
    formData.append('column_mapping', '{}');
    try {
      const { data } = await api.post('/companies/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      setPreview(null);
      toast.success(`Imported ${data.success} of ${data.total} companies`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.csv')) {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileRef.current.files = dt.files;
      handleFileSelect({ target: { files: dt.files } });
    }
  };

  return (
    <div data-testid="imports-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">Imports</h1>
        <p className="text-sm text-gray-500 mt-1">Import companies from CSV files. The system auto-detects column names, normalizes domains, checks for duplicates, and scores each imported company.</p>
      </div>

      {/* Upload area */}
      <div
        className="border-2 border-dashed border-gray-300 rounded-md bg-white p-8 text-center mb-6 hover:border-gray-400 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        data-testid="csv-upload-area"
      >
        <UploadSimple size={32} className="text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-600 mb-1">Drag & drop a CSV file here, or click to browse</p>
        <p className="text-xs text-gray-400">Supports .csv files with company data</p>
        <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
      </div>

      {/* Preview */}
      {preview && (
        <div className="border border-gray-200 rounded-md bg-white p-6 mb-6" data-testid="import-preview">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText size={20} className="text-gray-600" />
              <span className="text-sm font-medium text-gray-900">{preview.fileName}</span>
              <span className="text-xs text-gray-500">{preview.row_count} rows</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreview(null)}>Cancel</Button>
              <Button size="sm" onClick={handleImport} disabled={uploading} className="bg-black text-white hover:bg-gray-800" data-testid="confirm-import-btn">
                {uploading ? 'Importing...' : `Import ${preview.row_count} rows`}
              </Button>
            </div>
          </div>
          <div className="text-xs font-mono text-gray-500 mb-2">Columns: {preview.columns.join(', ')}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {preview.columns.map(col => (
                    <th key={col} className="py-2 px-3 text-left font-medium text-gray-500 uppercase tracking-wider">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.slice(0, 5).map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    {preview.columns.map(col => (
                      <td key={col} className="py-2 px-3 text-gray-700 truncate max-w-[200px]">{row[col] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div className="border border-gray-200 rounded-md bg-white p-6 mb-6" data-testid="import-result">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Import Result</h3>
          <div className="flex gap-6 mb-3">
            <div className="flex items-center gap-2"><Check size={16} className="text-emerald-600" /><span className="text-sm">{importResult.success} imported</span></div>
            <div className="flex items-center gap-2"><Warning size={16} className="text-amber-600" /><span className="text-sm">{importResult.skipped} skipped</span></div>
            <div className="flex items-center gap-2"><Warning size={16} className="text-red-600" /><span className="text-sm">{importResult.failed} failed</span></div>
          </div>
          <p className="text-xs text-gray-500 mt-3">What happens next: Imported companies are automatically scored. Check the Companies page to review and approve them. High-scoring leads with valid emails will appear in the Review Queue.</p>
          {importResult.errors && importResult.errors.length > 0 && (
            <div className="space-y-1 mt-2">
              {importResult.errors.map((err, i) => (
                <p key={i} className="text-xs text-red-600 font-mono">Row {err.row}: {err.reason}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Import history */}
      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500">Import History</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/50">
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Name</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Source</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Records</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Success</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Status</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="py-8 text-center text-gray-400">Loading...</td></tr>
            ) : batches.length === 0 ? (
              <tr><td colSpan="6" className="py-8 text-center text-gray-400">No imports yet</td></tr>
            ) : batches.map(b => (
              <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm text-gray-900">{b.name}</td>
                <td className="py-3 px-4 text-sm text-gray-600">{b.source_type}</td>
                <td className="py-3 px-4 text-sm font-mono text-gray-700">{b.record_count}</td>
                <td className="py-3 px-4 text-sm font-mono text-emerald-700">{b.success_count}</td>
                <td className="py-3 px-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${b.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{b.status}</span>
                </td>
                <td className="py-3 px-4 text-xs font-mono text-gray-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
